import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useGetUmbracoUserGroupsQuery, useEditRolesMutation } from '../../api/userManagementApi';
import type { UserManagementModal } from '../../commands/types';

type EditRolesDialogProps = {
  open: boolean;
  modal: UserManagementModal | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

const normalizeRole = (role: string) => role.trim().toLowerCase();

export const EditRolesDialog = ({
  open,
  modal,
  onClose,
  onSuccess,
  onError,
}: EditRolesDialogProps) => {
  const relationKeys = modal?.relationKeys ?? [];

  const { data: availableRoles = [], isFetching } = useGetUmbracoUserGroupsQuery(
    { relationKeys },
    { skip: !open || relationKeys.length === 0 }
  );

  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(
    () => new Set(modal?.currentRoles ?? [])
  );
  const [editRoles, { isLoading: isSaving }] = useEditRolesMutation();

  const allSelected = useMemo(
    () =>
      availableRoles.length > 0 &&
      availableRoles.every((role) => {
        const normalizedRole = normalizeRole(role);
        return Array.from(selectedRoles).some(
          (selectedRole) => normalizeRole(selectedRole) === normalizedRole
        );
      }),
    [availableRoles, selectedRoles]
  );

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      const normalizedRole = normalizeRole(role);
      const selectedRole = Array.from(next).find(
        (candidate) => normalizeRole(candidate) === normalizedRole
      );

      if (selectedRole) next.delete(selectedRole);
      else next.add(role);
      return next;
    });
  };

  const handleSave = async () => {
    if (!modal) return;
    try {
      const availableRoleByKey = new Map(availableRoles.map((role) => [normalizeRole(role), role]));
      const res = await editRoles({
        nodeKey: modal.nodeKey,
        relationKeys,
        roles: Array.from(
          selectedRoles,
          (role) => availableRoleByKey.get(normalizeRole(role)) ?? role
        ),
      }).unwrap();

      if (!res.success) {
        onError(res.message);
        return;
      }

      onSuccess(res.message);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update roles');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Roles</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 240 }}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : availableRoles.length === 0 ? (
          <Alert severity="info">No roles available.</Alert>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                size="small"
                onClick={() => setSelectedRoles(new Set(availableRoles))}
                disabled={allSelected}
              >
                Select All
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedRoles(new Set())}
                disabled={selectedRoles.size === 0}
              >
                Clear All
              </Button>
            </Stack>
            {availableRoles.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={Array.from(selectedRoles).some(
                      (selectedRole) => normalizeRole(selectedRole) === normalizeRole(role)
                    )}
                    onChange={() => toggleRole(role)}
                  />
                }
                label={role}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || isFetching}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRolesDialog;
